import type { FeatureUsageResponse, FeatureUsageStatus, FeatureUsageView } from '@/app/commons/generated'
import { getDuprApi } from '@/app/commons/network/duprApiCore'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

/**
 * Server feature keys — mirror the backend `FeatureDefinition.KEY` constants.
 * The backend is the source of truth for grants, caps, and windows; the client
 * only reads usage and spends active-set slots.
 */
export const SERVER_FEATURE_KEYS = {
  matchForecast: 'match-forecast',
  profileView: 'profile-view',
} as const

export type ServerFeatureKey = (typeof SERVER_FEATURE_KEYS)[keyof typeof SERVER_FEATURE_KEYS]

/** Shared query key for the whole-account feature-usage snapshot. */
export const serverFeatureUsageQueryKey = ['featureUsage', 'server'] as const

// ---------------------------------------------------------------------------
// Local narrowing shapes: the generated union subtypes are self-referential
// aliases (e.g. `CountFeatureUsageStatus = FeatureUsageStatus`), so the union
// carries no variant fields at the type level. These mirror the wire schema.
// ---------------------------------------------------------------------------

interface ResetMarkerLike {
  type?: string
  atEpochSeconds?: number
  nextAtEpochSeconds?: number
}

/** Shape read off a `count` status without depending on the circular alias. */
interface CountStatusLike {
  type: 'count'
  used?: number
  remaining?: number
  limit?: { type?: string; max?: number }
  reset?: ResetMarkerLike
}

/** Shape of an `active-set` status (set quota holding specific resources). */
interface ActiveSetStatusLike {
  type: 'active-set'
  capacity?: number
  used?: number
  remaining?: number
  entries?: Array<{ resourceId?: string }>
  reset?: ResetMarkerLike
}

/**
 * Body of `POST /api/features/{featureKey}/unlock/{resourceId}` — the generated
 * binding types the response as `object` (the controller returns a star
 * projection), so this narrows the singular `result` wrapper by hand.
 */
interface FeatureUnlockResult {
  featureKey?: string
  resourceId?: string
  unlocked?: boolean
  status?: FeatureUsageStatus
}

// ---------------------------------------------------------------------------
// Flattened per-feature usage for the UI.
// ---------------------------------------------------------------------------

/**
 * One server feature's usage, flattened from the discriminated
 * `FeatureUsageStatus` into the handful of fields the UI needs.
 *
 * `hasGrant` is false when no rule grants the feature to this user — the
 * feature is then open and must never be gated. `isUnlimited` is true for an
 * uncapped grant. `limitReached` is only ever true for a granted, capped
 * feature, so it is safe to gate on directly.
 *
 * A usage-count feature spends anonymous units; an active-set feature holds
 * specific resources, enumerated in `heldResourceIds` — re-acquiring a held
 * resource is free.
 */
export interface ServerFeatureUsage {
  hasGrant: boolean
  isUnlimited: boolean
  used: number
  limit: number | null
  remaining: number
  limitReached: boolean
  /** Resource ids currently held against an active-set quota; empty otherwise. */
  heldResourceIds: string[]
  /** When the allotment next replenishes (epoch seconds), or null when nothing replenishes. */
  resetAtEpochSeconds: number | null
  isLoading: boolean
}

const UNGRANTED: Omit<ServerFeatureUsage, 'isLoading'> = {
  hasGrant: false,
  isUnlimited: false,
  used: 0,
  limit: null,
  remaining: Number.POSITIVE_INFINITY,
  limitReached: false,
  heldResourceIds: [],
  resetAtEpochSeconds: null,
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

/**
 * Next replenish instant: a calendar window sends a fixed `at`, a rolling
 * window sends `rolling-refill`'s next age-out. Null for `never`.
 */
function readResetEpochSeconds(reset: ResetMarkerLike | undefined): number | null {
  if (reset?.type === 'at') return numberOrNull(reset.atEpochSeconds)
  if (reset?.type === 'rolling-refill') return numberOrNull(reset.nextAtEpochSeconds)
  return null
}

/** Reads the numeric cap off a count status, or null when it isn't a fixed max. */
function readCountLimit(limit: CountStatusLike['limit']): number | null {
  if (limit?.type !== 'limited') return null
  return numberOrNull(limit.max)
}

function readHeldResourceIds(entries: ActiveSetStatusLike['entries']): string[] {
  return (entries ?? []).map((entry) => entry.resourceId).filter((id): id is string => typeof id === 'string')
}

function deriveCount(status: CountStatusLike, isLoading: boolean): ServerFeatureUsage {
  const remaining = Math.max(0, status.remaining ?? 0)
  return {
    hasGrant: true,
    isUnlimited: false,
    used: status.used ?? 0,
    limit: readCountLimit(status.limit),
    remaining,
    limitReached: remaining <= 0,
    heldResourceIds: [],
    resetAtEpochSeconds: readResetEpochSeconds(status.reset),
    isLoading,
  }
}

function deriveActiveSet(status: ActiveSetStatusLike, isLoading: boolean): ServerFeatureUsage {
  const remaining = Math.max(0, status.remaining ?? 0)
  return {
    hasGrant: true,
    isUnlimited: false,
    used: status.used ?? 0,
    limit: numberOrNull(status.capacity),
    remaining,
    limitReached: remaining <= 0,
    heldResourceIds: readHeldResourceIds(status.entries),
    resetAtEpochSeconds: readResetEpochSeconds(status.reset),
    isLoading,
  }
}

/** Flattens a granted status; a `no-grant` (or unknown) status maps to open/ungated. */
function deriveGranted(status: FeatureUsageStatus, isLoading: boolean): ServerFeatureUsage {
  if (status.type === 'count') return deriveCount(status as unknown as CountStatusLike, isLoading)
  if (status.type === 'active-set') return deriveActiveSet(status as unknown as ActiveSetStatusLike, isLoading)
  if (status.type === 'unlimited') return { ...UNGRANTED, hasGrant: true, isUnlimited: true, isLoading }
  return { ...UNGRANTED, isLoading }
}

/** Flattens a feature view's wire status into {@link ServerFeatureUsage}. */
function deriveUsage(view: FeatureUsageView | undefined, isLoading: boolean): ServerFeatureUsage {
  const status = view?.status
  return status ? deriveGranted(status, isLoading) : { ...UNGRANTED, isLoading }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Reads this account's live feature-usage snapshot from `GET /api/features/usage`
 * through one shared TanStack Query cache entry. The endpoint is informational —
 * it never blocks or debits — so it is safe to call on render to drive
 * "remaining" displays and proactive gates. Disabled (fail-open) when logged out.
 */
export function useServerFeatureUsage() {
  const { token } = useAppSelector(authSelector)
  return useQuery({
    queryKey: serverFeatureUsageQueryKey,
    queryFn: async (): Promise<FeatureUsageResponse | undefined> => {
      const res = await getDuprApi().featureUsage.getUsage()
      return res.data.results?.[0]
    },
    // Usage is live and cheap; a short stale window keeps counters fresh
    // without refetching on every render.
    staleTime: 30_000,
    enabled: !!token,
  })
}

/** This account's usage for a single server feature. */
export function useServerFeatureUsageFor(featureKey: ServerFeatureKey): ServerFeatureUsage {
  const { data, isLoading, isFetching } = useServerFeatureUsage()
  const view = data?.features?.find((f) => f.featureKey === featureKey)
  return deriveUsage(view, isLoading && isFetching)
}

/**
 * Returns a callback that refetches the usage snapshot — call it after an
 * action that debits a server meter so displayed counts catch up.
 */
export function useInvalidateServerFeatureUsage(): () => void {
  const queryClient = useQueryClient()
  return useCallback(() => void queryClient.invalidateQueries({ queryKey: serverFeatureUsageQueryKey }), [queryClient])
}

/** Replaces one feature's status inside the cached usage snapshot. */
function patchFeatureStatus(
  snapshot: FeatureUsageResponse | undefined,
  featureKey: string,
  status: FeatureUsageStatus
): FeatureUsageResponse | undefined {
  if (!snapshot) return snapshot
  return {
    ...snapshot,
    features: snapshot.features.map((f) => (f.featureKey === featureKey ? { ...f, status } : f)),
  }
}

/** Writes an unlock's fresh status into the shared cache, or refetches without one. */
function applyUnlockResult(
  queryClient: ReturnType<typeof useQueryClient>,
  invalidate: () => void,
  featureKey: ServerFeatureKey,
  status: FeatureUsageStatus | undefined
): void {
  if (!status) {
    invalidate()
    return
  }
  queryClient.setQueryData<FeatureUsageResponse | undefined>(serverFeatureUsageQueryKey, (prev) =>
    patchFeatureStatus(prev, featureKey, status)
  )
}

/** Calls the unlock endpoint and narrows its singular `result` wrapper. */
async function postUnlock(featureKey: ServerFeatureKey, resourceId: string) {
  const res = await getDuprApi().featureUsage.unlock(featureKey, resourceId)
  const wrapper = res.data as { result?: FeatureUnlockResult }
  return wrapper.result
}

/**
 * Returns a recorder that spends one slot of an active-set feature on
 * `resourceId` via `POST /api/features/{featureKey}/unlock/{resourceId}`.
 * Idempotent server-side — re-unlocking a held resource never re-charges — and
 * exempt for the caller's own resource. The response carries the fresh quota
 * status, which is written straight into the shared usage cache so every
 * consumer re-renders; on error the snapshot is refetched instead.
 */
export function useUnlockServerFeature(featureKey: ServerFeatureKey): (resourceId: string) => void {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateServerFeatureUsage()

  const { mutate } = useMutation({
    mutationFn: (resourceId: string) => postUnlock(featureKey, resourceId),
    onSuccess: (unlock) => applyUnlockResult(queryClient, invalidate, featureKey, unlock?.status),
    onError: invalidate,
  })

  return useCallback((resourceId: string) => mutate(resourceId), [mutate])
}
